// settingsConstants.js
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiDroplet, FiZap, FiWind, FiCloud, FiTerminal, FiSun, FiMoon } = FiIcons;

/**
 * 10 New-Age Curated Themes for MeMark
 * Spans luxury dark modes, cyberpunk aesthetics, and clean scandinavian light modes.
 */
export const THEME_PRESETS = [
  {
    id: 'neo_emerald',
    name: 'Neo Emerald',
    tagline: 'Sovereign Enclave',
    mode: 'dark',
    accent: '#10B981',
    description: 'Deep obsidian enclave with radiant emerald glow. Ultra-crisp, new-age, and sovereign.',
    previewColors: ['#070A09', '#10B981', '#34D399', '#0D1411'],
    colors: {
      accent: '#10B981',
      pageBg: '#070A09',
      card: '#0D1411E6',
      border: '#10B98133',
      surface: '#111D17E6',
      heading: '#FFFFFF',
      text: '#F8FAFC',
      muted: '#94A3B8',
      label: '#34D399',
      chatUserBg: '#10B981',
      chatUser: '#000000',
      chatAiBg: '#0D1411E6',
      chatAi: '#F8FAFC',
      aiButtonBg: '#10B981',
      aiButtonText: '#000000',
    },
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk Grid',
    tagline: 'Tokyo Neon Grid',
    mode: 'dark',
    accent: '#00F0FF',
    description: 'Electric cyan and violet highlights on midnight blue canvas.',
    previewColors: ['#050713', '#00F0FF', '#A855F7', '#0A0F26'],
    colors: {
      accent: '#00F0FF',
      pageBg: '#050713',
      card: '#0A0F26E6',
      border: '#00F0FF33',
      surface: '#0E1638E6',
      heading: '#FFFFFF',
      text: '#F8FAFC',
      muted: '#A5B4FC',
      label: '#38BDF8',
      chatUserBg: '#00F0FF',
      chatUser: '#000000',
      chatAiBg: '#0A0F26E6',
      chatAi: '#F8FAFC',
      aiButtonBg: '#00F0FF',
      aiButtonText: '#000000',
    },
  },
  {
    id: 'obsidian',
    name: 'Obsidian Stealth',
    tagline: 'Minimalist Carbon',
    mode: 'dark',
    accent: '#FAFAFA',
    description: 'Pure stealth carbon blacks with high-contrast monochrome typography.',
    previewColors: ['#09090B', '#FAFAFA', '#A1A1AA', '#18181B'],
    colors: {
      accent: '#FAFAFA',
      pageBg: '#09090B',
      card: '#18181BE6',
      border: '#27272A',
      surface: '#202024E6',
      heading: '#FAFAFA',
      text: '#F4F4F5',
      muted: '#A1A1AA',
      label: '#E4E4E7',
      chatUserBg: '#FAFAFA',
      chatUser: '#09090B',
      chatAiBg: '#18181BE6',
      chatAi: '#F4F4F5',
      aiButtonBg: '#FAFAFA',
      aiButtonText: '#09090B',
    },
  },
  {
    id: 'tokyo_sunset',
    name: 'Tokyo Sunset',
    tagline: 'Warm Neon Dusk',
    mode: 'dark',
    accent: '#F97316',
    description: 'Vibrant solar amber and rose gradients inspired by Shinjuku twilight.',
    previewColors: ['#0D0814', '#F97316', '#F43F5E', '#190E26'],
    colors: {
      accent: '#F97316',
      pageBg: '#0D0814',
      card: '#190E26E6',
      border: '#F9731633',
      surface: '#241438E6',
      heading: '#FFF1F2',
      text: '#FFE4E6',
      muted: '#FDA4AF',
      label: '#FB923C',
      chatUserBg: '#F97316',
      chatUser: '#000000',
      chatAiBg: '#190E26E6',
      chatAi: '#FFE4E6',
      aiButtonBg: '#F97316',
      aiButtonText: '#000000',
    },
  },
  {
    id: 'deep_abyss',
    name: 'Deep Abyss',
    tagline: 'Oceanic Sapphire',
    mode: 'dark',
    accent: '#38BDF8',
    description: 'Nautical midnight navy with crisp aquatic azure glass cards.',
    previewColors: ['#030712', '#38BDF8', '#2DD4BF', '#0B172B'],
    colors: {
      accent: '#38BDF8',
      pageBg: '#030712',
      card: '#0B172BE6',
      border: '#38BDF833',
      surface: '#0F213EE6',
      heading: '#F0F9FF',
      text: '#E0F2FE',
      muted: '#7DD3FC',
      label: '#38BDF8',
      chatUserBg: '#38BDF8',
      chatUser: '#000000',
      chatAiBg: '#0B172BE6',
      chatAi: '#E0F2FE',
      aiButtonBg: '#38BDF8',
      aiButtonText: '#000000',
    },
  },
  {
    id: 'cosmic_amethyst',
    name: 'Cosmic Amethyst',
    tagline: 'Deep Space Nebula',
    mode: 'dark',
    accent: '#C084FC',
    description: 'Starlight purple with iridescent cosmic amethyst highlights.',
    previewColors: ['#090514', '#C084FC', '#E879F9', '#160B2E'],
    colors: {
      accent: '#C084FC',
      pageBg: '#090514',
      card: '#160B2EE6',
      border: '#C084FC33',
      surface: '#1F103FE6',
      heading: '#FAF5FF',
      text: '#F3E8FF',
      muted: '#D8B4FE',
      label: '#C084FC',
      chatUserBg: '#C084FC',
      chatUser: '#000000',
      chatAiBg: '#160B2EE6',
      chatAi: '#F3E8FF',
      aiButtonBg: '#C084FC',
      aiButtonText: '#000000',
    },
  },
  {
    id: 'nordic_alabaster',
    name: 'Nordic Alabaster',
    tagline: 'Scandinavian Clean (Light)',
    mode: 'light',
    accent: '#059669',
    description: 'Crisp pearl white background with deep slate typography and pine emerald.',
    previewColors: ['#F8FAFC', '#059669', '#0F172A', '#FFFFFF'],
    colors: {
      accent: '#059669',
      pageBg: '#F8FAFC',
      card: '#FFFFFFFA',
      border: '#E2E8F0',
      surface: '#F1F5F9',
      heading: '#0F172A',
      text: '#1E293B',
      muted: '#64748B',
      label: '#059669',
      chatUserBg: '#059669',
      chatUser: '#FFFFFF',
      chatAiBg: '#F1F5F9',
      chatAi: '#1E293B',
      aiButtonBg: '#059669',
      aiButtonText: '#FFFFFF',
    },
  },
  {
    id: 'vogue_ivory',
    name: 'Vogue Quartz',
    tagline: 'Luxury Editorial (Light)',
    mode: 'light',
    accent: '#E11D48',
    description: 'Warm cashmere cream surface with rose gold accent and rich contrast.',
    previewColors: ['#FCFBFA', '#E11D48', '#1C1917', '#FFFFFF'],
    colors: {
      accent: '#E11D48',
      pageBg: '#FCFBFA',
      card: '#FFFFFFFA',
      border: '#F2E8E5',
      surface: '#FBF0EE',
      heading: '#1C1917',
      text: '#292524',
      muted: '#78716C',
      label: '#E11D48',
      chatUserBg: '#E11D48',
      chatUser: '#FFFFFF',
      chatAiBg: '#FBF0EE',
      chatAi: '#292524',
      aiButtonBg: '#E11D48',
      aiButtonText: '#FFFFFF',
    },
  },
  {
    id: 'glacier_ice',
    name: 'Glacier Blue',
    tagline: 'Arctic Clarity (Light)',
    mode: 'light',
    accent: '#0284C7',
    description: 'Pure cool white with icy sapphire accents and crystal clean borders.',
    previewColors: ['#F0F7FF', '#0284C7', '#0C4A6E', '#FFFFFF'],
    colors: {
      accent: '#0284C7',
      pageBg: '#F0F7FF',
      card: '#FFFFFFFA',
      border: '#BAE6FD',
      surface: '#E0F2FE',
      heading: '#0C4A6E',
      text: '#0369A1',
      muted: '#0284C7',
      label: '#0284C7',
      chatUserBg: '#0284C7',
      chatUser: '#FFFFFF',
      chatAiBg: '#E0F2FE',
      chatAi: '#0369A1',
      aiButtonBg: '#0284C7',
      aiButtonText: '#FFFFFF',
    },
  },
  {
    id: 'solar_paper',
    name: 'Solar Minimal',
    tagline: 'High Contrast Ink (Light)',
    mode: 'light',
    accent: '#EA580C',
    description: 'Warm paper texture aesthetic with vibrant solar amber and deep ink.',
    previewColors: ['#FAF7F5', '#EA580C', '#1C1917', '#FFFFFF'],
    colors: {
      accent: '#EA580C',
      pageBg: '#FAF7F5',
      card: '#FFFFFFFA',
      border: '#FED7AA',
      surface: '#FFEDD5',
      heading: '#1C1917',
      text: '#292524',
      muted: '#78716C',
      label: '#EA580C',
      chatUserBg: '#EA580C',
      chatUser: '#FFFFFF',
      chatAiBg: '#FFEDD5',
      chatAi: '#292524',
      aiButtonBg: '#EA580C',
      aiButtonText: '#FFFFFF',
    },
  },
];

/**
 * Animated and Atmospheric Background choices.
 */
export const ANIMATED_BACKGROUNDS = [
  {
    id: 'none',
    name: 'Minimal Zen (Clean)',
    description: 'Clean subtle gradient mesh with zero distractions and maximum performance',
    icon: FiMoon,
  },
  {
    id: 'constellation',
    name: 'Constellation Stars',
    description: 'Connected star particles drifting in geometric space',
    icon: FiStar,
  },
  {
    id: 'aurora',
    name: 'Aurora Borealis',
    description: 'Flowing northern atmospheric lights and radiant waves',
    icon: FiWind,
  },
  {
    id: 'cosmic',
    name: 'Cosmic Nebula',
    description: 'Deep galaxy dust and floating stellar clusters',
    icon: FiStar,
  },
  {
    id: 'matrix',
    name: 'Matrix Rain',
    description: 'Digital phosphor code cascades for hacker aesthetic',
    icon: FiTerminal,
  },
  {
    id: 'fireflies',
    name: 'Fireflies',
    description: 'Glowing warm drifting embers and ambient points of light',
    icon: FiZap,
  },
  {
    id: 'ripple',
    name: 'Water Ripple',
    description: 'Expanding wave rings and liquid physics',
    icon: FiDroplet,
  },
  {
    id: 'blob',
    name: 'Liquid Blobs',
    description: 'Liquid chromatic morphing shapes',
    icon: FiCloud,
  },
];

/**
 * Single source of truth for editable color tokens.
 * All values are hex (#RGB / #RRGGBB / #RRGGBBAA).
 */
export const FONT_TARGETS = [
  { key: 'accent', label: 'Accent', desc: 'Primary accent color used across UI' },
  { key: 'heading', label: 'Heading', desc: 'Main greeting / page title' },
  { key: 'text', label: 'Body Text', desc: 'General text & folder names' },
  { key: 'muted', label: 'Subtitle', desc: 'Welcome subtitle & hints' },
  { key: 'label', label: 'Labels', desc: 'Section labels & badges' },

  { key: 'chatUserBg', label: 'Chat Bubble (You)', desc: 'Your chat bubble background color' },
  { key: 'chatUser', label: 'Chat Text (You)', desc: 'Your message text color' },
  { key: 'chatAiBg', label: 'Chat Bubble (AI)', desc: 'AI chat bubble background color' },
  { key: 'chatAi', label: 'Chat Text (AI)', desc: 'AI message text color' },

  { key: 'aiButtonBg', label: 'AI Base Color', desc: 'Background color of AI buttons & avatar' },
  { key: 'aiButtonText', label: 'AI Icon Color', desc: 'Inner icon color of AI buttons & avatar' },

  { key: 'pageBg', label: 'Page Background', desc: 'Page background color (behind animation)' },
  { key: 'card', label: 'Card Background', desc: 'Card / panel glass background color' },
  { key: 'border', label: 'Border', desc: 'Default border color' },
  { key: 'surface', label: 'Surface', desc: 'Subtle surface / tile background color' },
];